import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero, GameIcon, SegmentedControl } from "../../../components/game";
import {
  getCoachEvents,
  getEventTeams,
  inviteAthletes,
  removeInvitation,
  getEvent,
  addEventNote,
  getEventNotes,
  type Event,
  type EventInvitation,
  type EventTeamWithAthletes,
  type EventNote,
} from "../../../services/api";

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700",
  published: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-700",
};

const invitationStatusColors: Record<string, string> = {
  invited: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
};

export default function AppCoachEvents() {
  const { t } = useTranslation();
  const { activeClubId, clubs } = useAuth();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  // Event details modal
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"invitations" | "invite" | "notes">("invitations");

  // Invitations for selected event
  const [eventInvitations, setEventInvitations] = useState<EventInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // Teams and athletes for inviting
  const [teams, setTeams] = useState<EventTeamWithAthletes[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<string[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);

  // Notes
  const [notes, setNotes] = useState<EventNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [sendingNote, setSendingNote] = useState(false);

  const clubId = activeClubId || clubs[0]?.id;

  const statusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      draft: t("coach.eventStatusDraft", "Ciornă"),
      published: t("coach.eventStatusPublished", "Publicat"),
      cancelled: t("coach.eventStatusCancelled", "Anulat"),
      completed: t("coach.eventStatusCompleted", "Finalizat"),
    };
    return labels[status] || status;
  };

  const invitationStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      invited: t("coach.invStatusInvited", "Invitat"),
      confirmed: t("coach.invStatusConfirmed", "Confirmat"),
      declined: t("coach.invStatusDeclined", "Refuzat"),
    };
    return labels[status] || status;
  };

  useEffect(() => {
    if (clubId) {
      fetchEvents();
    }
  }, [clubId]);

  const fetchEvents = async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const data = await getCoachEvents(clubId);
      setEvents(data.events);
    } catch (error) {
      console.error("Failed to fetch events:", error);
      toast.error(t("coach.eventsLoadError", "Nu s-au putut incarca evenimentele"));
    }
    setLoading(false);
  };

  const openEventModal = async (event: Event) => {
    setSelectedEvent(event);
    setShowModal(true);
    setActiveTab("invitations");
    setSelectedTeamId("");
    setSelectedAthleteIds([]);
    setNewNote("");

    await Promise.all([
      loadEventInvitations(event.id),
      loadEventTeams(event.id),
      loadEventNotes(event.id),
    ]);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
    setEventInvitations([]);
    setTeams([]);
    setNotes([]);
  };

  const loadEventInvitations = async (eventId: string) => {
    setLoadingInvitations(true);
    try {
      const event = await getEvent(eventId);
      setEventInvitations(event.invitations || []);
    } catch (error) {
      console.error("Failed to load invitations:", error);
    }
    setLoadingInvitations(false);
  };

  const loadEventTeams = async (eventId: string) => {
    setLoadingTeams(true);
    try {
      const data = await getEventTeams(eventId);
      setTeams(data.teams);
    } catch (error) {
      console.error("Failed to load teams:", error);
    }
    setLoadingTeams(false);
  };

  const loadEventNotes = async (eventId: string) => {
    setLoadingNotes(true);
    try {
      const data = await getEventNotes(eventId);
      setNotes(data.notes);
    } catch (error) {
      console.error("Failed to load notes:", error);
    }
    setLoadingNotes(false);
  };

  const selectedTeam = teams.find(t => t.id === selectedTeamId);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthleteIds(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleSendInvitations = async () => {
    if (!selectedEvent || !selectedTeamId || selectedAthleteIds.length === 0) return;

    setSendingInvites(true);
    try {
      await inviteAthletes(selectedEvent.id, selectedTeamId, selectedAthleteIds);
      toast.success(t("coach.athletesInvited", "{{count}} sportivi invitati", { count: selectedAthleteIds.length }));
      setSelectedAthleteIds([]);

      // Refresh invitations and teams
      await Promise.all([
        loadEventInvitations(selectedEvent.id),
        loadEventTeams(selectedEvent.id),
      ]);
    } catch (error) {
      console.error("Failed to send invitations:", error);
      toast.error(t("coach.invitesSendError", "Nu s-au putut trimite invitațiile"));
    }
    setSendingInvites(false);
  };

  const handleRemoveInvitation = async (invitationId: string) => {
    if (!selectedEvent) return;

    try {
      await removeInvitation(selectedEvent.id, invitationId);
      toast.success(t("coach.invitationCancelled", "Invitație anulată"));
      await Promise.all([
        loadEventInvitations(selectedEvent.id),
        loadEventTeams(selectedEvent.id),
      ]);
    } catch (error) {
      console.error("Failed to remove invitation:", error);
      toast.error(t("coach.invitationCancelError", "Nu s-a putut anula invitația"));
    }
  };

  const handleSendNote = async () => {
    if (!selectedEvent || !newNote.trim()) return;

    setSendingNote(true);
    try {
      await addEventNote(selectedEvent.id, newNote.trim());
      toast.success(t("coach.noteAdded", "Nota adăugată"));
      setNewNote("");
      await loadEventNotes(selectedEvent.id);
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error(t("coach.noteAddError", "Nu s-a putut adauga nota"));
    }
    setSendingNote(false);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Check if coach has confirmed athletes (can add notes)
  const hasConfirmedAthletes = eventInvitations.some(inv => inv.status === "confirmed");

  // Get my invitations (invitations I sent)
  const myInvitations = eventInvitations;

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold">{t("coach.events", "Evenimente")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">{t("coach.eventsSubtitle", "Gestionează invitațiile la evenimente")}</p>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-[3px] border-indigo-500 rounded-full border-t-transparent animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
              <GameIcon name="trophy" className="w-8 h-8" />
            </div>
            <p className="text-slate-500">{t("events.no_events", "Nu există evenimente publicate")}</p>
          </GameCard>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <GameCard
                key={event.id}
                onClick={() => openEventModal(event)}
                className="p-4 cursor-pointer hover:border-indigo-200 active:translate-y-[2px] transition-all"
              >
                <div className="flex gap-4">
                  {event.photo_url ? (
                    <img
                      src={event.photo_url}
                      alt={event.title}
                      className="object-cover w-20 h-20 rounded-lg"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg">
                      <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-slate-800 line-clamp-1">{event.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[event.status] || "bg-slate-100 text-slate-700"}`}>
                        {statusLabel(event.status)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatDate(event.start_date)}</span>
                      {event.event_time && <span>@ {event.event_time}</span>}
                    </div>

                    {event.location_city && (
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span>{event.location_city}</span>
                      </div>
                    )}

                    {event.sport && (
                      <span className="inline-block mt-2 px-2 py-0.5 text-xs font-bold bg-indigo-50 text-indigo-600 rounded-full">
                        {event.sport.name}
                      </span>
                    )}
                  </div>
                </div>
              </GameCard>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Event Details Modal.
          The overlay reserves the chrome at BOTH ends — the status bar and
          notch above, the fixed bottom nav below — and the panel is capped at
          `max-h-full`, which for a flex item means the overlay's content box:
          exactly the space left between them.

          It used to be capped at `calc(100vh-9rem)`, a guess that did not
          include the top inset. An event with a long invitation list filled
          it, and since the panel is anchored to the bottom, the overflow went
          upwards — the header slid behind the status bar and took the close
          button with it, leaving a modal with no way out. No magic numbers
          now: the padding states the chrome, the cap follows from it. */}
      {showModal && selectedEvent && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-0 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-0"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg max-h-full sm:max-h-[90dvh] bg-white rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header. Only an event WITH a photo earns a tall banner — the
                placeholder gradient used to eat 128px of a short modal for
                nothing, which is what made the header read as big and empty.
                Without a photo it shrinks to a strip that just carries the
                close button. */}
            <div className="relative shrink-0">
              {selectedEvent.photo_url ? (
                <img
                  src={selectedEvent.photo_url}
                  alt={selectedEvent.title}
                  className="object-cover w-full h-28"
                />
              ) : (
                <div className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-500" />
              )}
              <button
                onClick={closeModal}
                className="absolute p-2 bg-white rounded-full shadow-lg top-2 right-3"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Everything below the banner shares ONE scroll container. Info
                and tabs used to be fixed rows, so a long description squeezed
                the tab list into a sliver that looked cut off. The tab bar
                stays pinned while the body scrolls under it. */}
            <div className="flex-1 overflow-y-auto overscroll-contain">

            {/* Event Info */}
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">{selectedEvent.title}</h2>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(selectedEvent.start_date)}
                  {selectedEvent.event_time && ` @ ${selectedEvent.event_time}`}
                </span>
                {selectedEvent.location_city && (
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    {selectedEvent.location_city}
                  </span>
                )}
              </div>
              {selectedEvent.description && (
                <p className="mt-2 text-sm text-slate-600">{selectedEvent.description}</p>
              )}
            </div>

            {/* Tabs */}
            <div className="sticky top-0 z-10 px-4 py-3 bg-white border-b border-slate-200">
              <SegmentedControl<"invitations" | "invite" | "notes">
                className="flex w-full [&>button]:flex-1"
                options={[
                  { value: "invitations", label: t("coach.invitationsTab", "Invitații ({{count}})", { count: myInvitations.length }) },
                  { value: "invite", label: t("coach.inviteAthletes", "Invita sportivi") },
                  { value: "notes", label: t("coach.notesTab", "Note ({{count}})", { count: notes.length }) },
                ]}
                value={activeTab}
                onChange={setActiveTab}
              />
            </div>

            {/* Tab Content. pb-10 leaves breathing room at the end so the
                last row doesn't sit flush against the modal edge. */}
            <div className="px-4 pt-4 pb-10">
              {/* Invitations Tab */}
              {activeTab === "invitations" && (
                <div>
                  {loadingInvitations ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="w-6 h-6 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin" />
                    </div>
                  ) : myInvitations.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">
                      <p>{t("coach.noInvitationsSent", "Nu ai trimis nicio invitație")}</p>
                      <button
                        onClick={() => setActiveTab("invite")}
                        className="mt-2 text-sm text-indigo-600 hover:underline"
                      >
                        {t("coach.inviteAthletes", "Invita sportivi")}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {myInvitations.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">
                              {inv.athlete?.full_name || inv.athlete?.email || "—"}
                            </p>
                            <p className="text-xs text-slate-500">{inv.team?.name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${invitationStatusColors[inv.status]}`}>
                              {invitationStatusLabel(inv.status)}
                            </span>
                            {inv.status === "invited" && (
                              <button
                                onClick={() => handleRemoveInvitation(inv.id)}
                                className="p-1 text-slate-400 hover:text-red-500"
                                title={t("coach.cancelInvitation", "Anulează invitație")}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Invite Tab */}
              {activeTab === "invite" && (
                <div>
                  {loadingTeams ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="w-6 h-6 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin" />
                    </div>
                  ) : teams.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">
                      <p>{t("coach.noTeamsAvailable", "Nu ai echipe disponibile")}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Team Selector */}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-slate-700">
                          {t("coach.selectTeam", "Selectează echipa")}
                        </label>
                        <select
                          value={selectedTeamId}
                          onChange={e => {
                            setSelectedTeamId(e.target.value);
                            setSelectedAthleteIds([]);
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">{t("coach.chooseTeamOption", "-- Alege echipa --")}</option>
                          {teams.map(team => (
                            <option key={team.id} value={team.id}>
                              {team.name} ({t("coach.athletesCount", "{{count}} sportivi", { count: team._count.athletes })})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Athletes List */}
                      {selectedTeam && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-slate-700">
                              {t("coach.athletesAvailable", "Sportivi ({{count}} disponibili)", { count: selectedTeam.athletes.filter(a => !a.alreadyInvited).length })}
                            </label>
                            {selectedAthleteIds.length > 0 && (
                              <span className="text-sm text-indigo-600">
                                {t("coach.selectedCount", "{{count}} selectati", { count: selectedAthleteIds.length })}
                              </span>
                            )}
                          </div>

                          <div className="space-y-1">
                            {selectedTeam.athletes.map(({ athlete_id, athlete, alreadyInvited }) => (
                              <label
                                key={athlete_id}
                                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${
                                  alreadyInvited
                                    ? "opacity-50 cursor-not-allowed bg-slate-100"
                                    : selectedAthleteIds.includes(athlete_id)
                                    ? "bg-indigo-50 border border-indigo-200"
                                    : "bg-slate-50 hover:bg-slate-100"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedAthleteIds.includes(athlete_id)}
                                  onChange={() => handleAthleteToggle(athlete_id)}
                                  disabled={alreadyInvited}
                                  className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                                />
                                <span className="flex-1 text-sm text-slate-800">
                                  {athlete.full_name || athlete.email || "—"}
                                </span>
                                {alreadyInvited && (
                                  <span className="text-xs text-slate-500">{t("coach.alreadyInvited", "Deja invitat")}</span>
                                )}
                              </label>
                            ))}
                          </div>

                          {/* Send Button */}
                          {selectedAthleteIds.length > 0 && (
                            <GameButton
                              variant="primary"
                              onClick={handleSendInvitations}
                              disabled={sendingInvites}
                              className="mt-4"
                            >
                              {sendingInvites ? (
                                <span className="flex items-center justify-center gap-2">
                                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                                  {t("coach.sending", "Se trimit...")}
                                </span>
                              ) : (
                                t("coach.sendInvitations", "Trimite {{count}} invitații", { count: selectedAthleteIds.length })
                              )}
                            </GameButton>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === "notes" && (
                <div>
                  {/* Add Note Form - only if has confirmed athletes */}
                  {hasConfirmedAthletes && (
                    <div className="mb-4">
                      <textarea
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        placeholder={t("coach.addNotePlaceholder", "Adauga o nota...")}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        rows={3}
                      />
                      <GameButton
                        variant="primary"
                        onClick={handleSendNote}
                        disabled={!newNote.trim() || sendingNote}
                        className="mt-2"
                      >
                        {sendingNote ? t("coach.addingNote", "Se adauga...") : t("coach.addNote", "Adauga nota")}
                      </GameButton>
                    </div>
                  )}

                  {!hasConfirmedAthletes && (
                    <div className="p-3 mb-4 text-sm text-amber-700 bg-amber-50 rounded-lg">
                      {t("coach.notesNeedConfirmed", "Poți adauga note după ce ai cel putin un sportiv confirmat.")}
                    </div>
                  )}

                  {/* Notes List */}
                  {loadingNotes ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="w-6 h-6 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin" />
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">
                      <p>{t("coach.noNotes", "Nu există note")}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notes.map(note => (
                        <div
                          key={note.id}
                          className={`p-3 rounded-lg ${
                            note.author_role === "club_admin"
                              ? "bg-blue-50 border-l-4 border-blue-500"
                              : "bg-emerald-50 border-l-4 border-emerald-500"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-medium ${
                              note.author_role === "club_admin" ? "text-blue-600" : "text-emerald-600"
                            }`}>
                              {note.author?.full_name || ""}
                              {note.author_role === "club_admin" && ` ${t("coach.adminSuffix", "(Admin)")}`}
                            </span>
                            <span className="text-xs text-slate-400">
                              {new Date(note.created_at).toLocaleString("ro-RO", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      )}
    </MobileAppLayout>
  );
}
